import React, { useEffect, useState } from 'react';
import './MultimetalSipPage.css';
import PageLayout from '../Components/PageLayout';

const CP_URL = 'https://coinbazaar.ebullion.in/';

const defaultMultiPrices = { Au: 1000, Ag: 500, Pt: 250, Pd: 250 };

const buildSecureUrlMulti = (multiPrices = defaultMultiPrices, isSip = true) => {
  const base = new URL('signin', CP_URL);
  // dist = comma-separated amounts in fixed order: Au,Ag,Pt,Pd
  const dist = ['Au', 'Ag', 'Pt', 'Pd']
    .map((code) => multiPrices[code] ?? 0)
    .join(',');
  const params = {
    action: isSip ? undefined : 'multimetal',
    dist,
    sip: isSip ? '1' : undefined,
    sipint: isSip ? 'monthly' : undefined,
  };
  Object.entries(params).forEach(([k, v]) => {
    if (v !== '' && v !== null && v !== undefined) base.searchParams.set(k, v);
  });
  return base.toString();
};

const startMultimetalSip = () => {
  window.location.href = buildSecureUrlMulti(defaultMultiPrices, true);
};

const comparisonVsGold = [
  { feature: 'Metals covered', multi: 'Gold, Silver, Platinum, Palladium', other: 'Gold only' },
  { feature: 'Diversification', multi: 'Across 4 precious metals', other: 'Single metal' },
  { feature: 'Allocation control', multi: 'Custom amount per metal', other: 'Fixed to gold' },
  { feature: 'Minimum investment', multi: 'From ₹10 per metal', other: 'From ₹10' },
  { feature: 'Investment frequency', multi: 'Weekly or monthly', other: 'Weekly or monthly' },
  { feature: 'Best suited for', multi: 'Investors wanting balanced metal exposure', other: 'Investors focused solely on gold' },
  { feature: 'Risk profile', multi: 'Spread across multiple metals', other: 'Concentrated in gold' },
];

const comparisonVsSilver = [
  { feature: 'Metals covered', multi: 'Gold, Silver, Platinum, Palladium', other: 'Silver only' },
  { feature: 'Diversification', multi: 'Across 4 precious metals', other: 'Single metal' },
  { feature: 'Allocation control', multi: 'Custom amount per metal', other: 'Fixed to silver' },
  { feature: 'Minimum investment', multi: 'From ₹10 per metal', other: 'From ₹10' },
  { feature: 'Investment frequency', multi: 'Weekly or monthly', other: 'Weekly or monthly' },
  { feature: 'Best suited for', multi: 'Investors wanting balanced metal exposure', other: 'Investors focused solely on silver' },
  { feature: 'Risk profile', multi: 'Spread across multiple metals', other: 'Concentrated in silver' },
];

const faqs = [
  {
    question: 'Which metals can I invest in through a Multi-Metal SIP?',
    answer:
      'Four precious metals — Gold, Silver, Platinum and Palladium. You can include all four or only the ones you want, and set a different amount for each.',
  },
  {
    question: 'What is the minimum amount to start a Multi-Metal SIP?',
    answer:
      'You can start from just ₹10 per metal, which makes it accessible for beginners and anyone who wants to start small and build up gradually over time.',
  },
  {
    question: 'Can I invest in Gold and Silver together?',
    answer:
      'Yes. You can select both Gold and Silver in the same SIP and allocate a separate amount to each — for example, ₹2,000 in Gold and ₹1,000 in Silver every month. Both run through one SIP setup, and you can add Platinum and Palladium too for broader diversification.',
  },
  {
    question: 'How can I track my holdings?',
    answer:
      'All your metal holdings appear in one place on the CoinBazaar powered by eBullion platform, giving you a consolidated view of your precious metals portfolio.',
  },
  {
    question: 'Who should consider a Multi-Metal SIP?',
    answer:
      "It suits investors who want to diversify across precious metals without managing several separate plans, who want full control over their allocation, or who aren't sure which metal will perform best and prefer exposure to more than one. It works for first-time investors and long-term wealth builders alike.",
  },
];

function ComparisonTable({ title, rightColumnLabel, rows }) {
  return (
    <div className="mm-table-wrap">
      <table className="mm-comparison-table">
        <caption className="mm-table-caption">{title}</caption>
        <thead>
          <tr>
            <th scope="col">Feature</th>
            <th scope="col">Multi-Metal SIP</th>
            <th scope="col">{rightColumnLabel}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.feature}>
              <th scope="row">{row.feature}</th>
              <td>{row.multi}</td>
              <td>{row.other}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const FaqAccordion = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (index) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  return (
    <div className="mmsip-faq-list">
      {faqs.map((faq, index) => (
        <div
          key={index}
          className={`mmsip-faq-item${openIndex === index ? ' mmsip-faq-item--open' : ''}`}
        >
          <button
            className="mmsip-faq-question"
            onClick={() => toggle(index)}
            aria-expanded={openIndex === index}
          >
            <span className="mmsip-faq-num">{index + 1}.</span>
            <span className="mmsip-faq-qtext">{faq.question}</span>
            <span className="mmsip-faq-icon">{openIndex === index ? '−' : '+'}</span>
          </button>
          {openIndex === index && (
            <div className="mmsip-faq-answer">{faq.answer}</div>
          )}
        </div>
      ))}
    </div>
  );
};

const MultimetalSipPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <PageLayout page="multimetal-sip">
      <div className="mmsip-wrapper">
        <div className="mmsip-container">

          {/* ── Page Header ── */}
          <div className="mmsip-page-header">
            <span className="mmsip-eyebrow">Digital Precious Metals</span>
            <h1 className="mmsip-page-title">
              Multi-Metal SIP: Invest in Gold, Silver, Platinum and Palladium Through a Single SIP
            </h1>
            <p className="mmsip-page-subtitle">
              Why limit yourself to just one precious metal when you can invest in multiple?
              CoinBazaar powered by eBullion's Multi-Metal SIP allows you to invest in Gold,
              Silver, Platinum and Palladium simultaneously through a single SIP setup.
            </p>
            <button className="mmsip-cta-btn" onClick={startMultimetalSip}>
              Start Multi-Metal SIP
            </button>
          </div>

          {/* ── Intro ── */}
          <div className="mmsip-section">
            <p className="mmsip-p">
              Instead of creating separate SIPs for each metal, you can choose the amount you
              wish to invest in each metal and build your own precious metals portfolio with
              ease.
            </p>
            <p className="mmsip-p">
              Whether you're looking to invest in Gold and Silver together, diversify across
              precious metals, or create a disciplined long-term investment plan, Multi-Metal
              SIP offers the flexibility to invest according to your goals. With a minimum
              investment of just ₹10 per metal, investors can start small and gradually build a
              diversified precious metals portfolio over time.
            </p>
          </div>

          {/* ── What is a Multi-Metal SIP ── */}
          <div className="mmsip-section">
            <h2 className="mmsip-h2">What is a Multi-Metal SIP?</h2>
            <p className="mmsip-p">
              A Multi-Metal SIP is a systematic investment plan that allows investors to invest
              regularly in multiple precious metals through a single investment journey. Unlike
              a traditional Gold SIP or Silver SIP, where investments are restricted to a single
              metal, a Multi-Metal SIP enables investors to simultaneously invest in Gold,
              Silver, Platinum and Palladium.
            </p>
            <p className="mmsip-p">
              The unique advantage of a Multi-Metal SIP is flexibility. Investors can decide
              exactly how much they want to invest in each metal based on their investment
              objectives, budget and market outlook. For example, one investor may choose to
              invest ₹1,000 in Gold and ₹500 in Silver every month, while another may prefer to
              split investments across all four precious metals.
            </p>
            <p className="mmsip-p">
              This gives investors complete control over their precious metals portfolio while
              maintaining the convenience of systematic investing.
            </p>
          </div>

          {/* ── How it works ── */}
          <div className="mmsip-section">
            <h2 className="mmsip-h2">How Does a Multi-Metal SIP Work?</h2>
            <p className="mmsip-p">
              Setting up a Multi-Metal SIP is simple and convenient. Investors begin by
              selecting the metals they wish to include in their SIP. They can then choose a
              separate investment amount for Gold, Silver, Platinum and Palladium.
            </p>
            <p className="mmsip-p">For example, an investor may decide to invest:</p>
            <ul className="mmsip-list">
              <li>₹1,000 in Gold</li>
              <li>₹500 in Silver</li>
              <li>₹250 in Platinum</li>
              <li>₹250 in Palladium</li>
            </ul>
            <p className="mmsip-p">
              This creates a total SIP value of ₹2,000 per month while providing exposure to
              four different precious metals.
            </p>
            <p className="mmsip-p">
              Once the SIP is activated, investments are automatically processed according to
              the selected frequency. Investors can choose to invest weekly or monthly,
              depending on their preference. Over time, they accumulate holdings across multiple
              metals while benefiting from disciplined investing and rupee cost averaging.
            </p>
            <p className="mmsip-p">
              The entire portfolio can be tracked through the CoinBazaar powered by eBullion
              platform, giving investors a consolidated view of their precious metals holdings
              in one place.
            </p>
          </div>

          {/* ── Why invest in multiple metals ── */}
          <div className="mmsip-section">
            <h2 className="mmsip-h2">Why Invest in Multiple Precious Metals?</h2>
            <p className="mmsip-p">
              Every precious metal has unique characteristics and demand drivers. While Gold is
              often viewed as a store of value and a long-term wealth preservation asset, Silver
              benefits from both investment demand and industrial usage. Platinum and Palladium
              are widely used across industrial and automotive sectors and often respond to
              different economic factors.
            </p>
            <p className="mmsip-p">
              Because each metal behaves differently under varying market conditions, many
              investors prefer diversification rather than concentrating all their investments
              in a single metal. Investing in multiple precious metals can help create a more
              balanced portfolio while providing exposure to different market opportunities.
            </p>
            <p className="mmsip-p">
              A Multi-Metal SIP makes diversification easier by allowing investors to build
              positions across Gold, Silver, Platinum and Palladium through one simple
              investment plan.
            </p>
          </div>

          {/* ── Benefits ── */}
          <div className="mmsip-section">
            <h2 className="mmsip-h2">Benefits of a Multi-Metal SIP</h2>
            <p className="mmsip-p">
              One of the biggest benefits of a Multi-Metal SIP is flexibility. Investors are not
              restricted to a predefined allocation and can choose exactly how much they want to
              invest in each precious metal.
            </p>
            <p className="mmsip-p">
              The SIP structure also promotes investment discipline. By investing regularly,
              investors can gradually build their holdings without worrying about timing the
              market. This approach can help reduce the impact of short-term price fluctuations
              and encourage long-term wealth creation.
            </p>
            <p className="mmsip-p">
              Another important advantage is convenience. Instead of managing separate SIPs for
              different metals, investors can create and manage their precious metals portfolio
              through a single platform. This makes tracking investments simpler and more
              efficient.
            </p>
            <p className="mmsip-p">
              The ability to start from just ₹10 per metal also makes Multi-Metal SIP accessible
              to a wide range of investors, including beginners who want to start investing in
              precious metals with a small amount.
            </p>
          </div>

          {/* ── Gold + Silver together ── */}
          <div className="mmsip-section">
            <h2 className="mmsip-h2">Can I Invest in Gold and Silver Together?</h2>
            <p className="mmsip-p">
              Yes. One of the most common questions investors ask is whether they can invest in
              Gold and Silver together through a single SIP. With CoinBazaar powered by
              eBullion's Multi-Metal SIP, investors can choose both Gold and Silver and allocate
              separate investment amounts to each metal.
            </p>
            <p className="mmsip-p">
              For example, an investor may choose to invest ₹2,000 in Gold and ₹1,000 in Silver
              every month. Both investments are executed through the same SIP setup, making it
              easier to build exposure to multiple precious metals without maintaining separate
              investment plans.
            </p>
            <p className="mmsip-p">
              Investors can also add Platinum and Palladium to further diversify their
              portfolio if they wish.
            </p>
          </div>

          {/* ── Comparison: vs Gold SIP ── */}
          <div className="mmsip-section">
            <h2 className="mmsip-h2">Multi-Metal SIP vs Gold SIP</h2>
            <p className="mmsip-p">
              A Gold SIP suits investors who want dedicated exposure to a single metal and
              prefer to focus purely on accumulating gold. A Multi-Metal SIP suits those who
              want broader exposure across the precious metals market while still deciding their
              own allocation. The table below shows how the two compare.
            </p>
            <ComparisonTable
              title="Multi-Metal SIP vs Gold SIP"
              rightColumnLabel="Gold SIP"
              rows={comparisonVsGold}
            />
            <p className="mmsip-p">
              For investors who believe in diversification, a Multi-Metal SIP can act as a
              complement or an alternative to a traditional Gold SIP.
            </p>
          </div>

          {/* ── Comparison: vs Silver SIP ── */}
          <div className="mmsip-section">
            <h2 className="mmsip-h2">Multi-Metal SIP vs Silver SIP</h2>
            <p className="mmsip-p">
              A Silver SIP is designed for investors seeking direct exposure to silver prices
              alone. A Multi-Metal SIP keeps that silver exposure while adding gold, platinum
              and palladium, so the portfolio responds to a wider set of market conditions. The
              comparison is below.
            </p>
            <ComparisonTable
              title="Multi-Metal SIP vs Silver SIP"
              rightColumnLabel="Silver SIP"
              rows={comparisonVsSilver}
            />
            <p className="mmsip-p">
              This lets investors keep silver exposure while building a customised precious
              metals portfolio aligned to their goals.
            </p>
          </div>

          {/* ── Who should invest ── */}
          <div className="mmsip-section">
            <h2 className="mmsip-h2">Who Should Invest in a Multi-Metal SIP?</h2>
            <p className="mmsip-p">
              A Multi-Metal SIP is suitable for investors who want to diversify across precious
              metals without managing multiple investment plans. It can be particularly useful
              for investors who are unsure whether Gold, Silver, Platinum or Palladium will
              perform better in the future and prefer exposure to multiple metals.
            </p>
            <p className="mmsip-p">
              The product is also ideal for investors who want complete control over their SIP
              allocations. Since investors decide how much to invest in each metal, they can
              build a portfolio that reflects their personal investment strategy.
            </p>
            <p className="mmsip-p">
              Whether you are a first-time investor, a long-term wealth builder or someone
              looking to diversify your portfolio, a Multi-Metal SIP offers a flexible and
              convenient way to invest in precious metals.
            </p>
          </div>

          {/* ── Start ── */}
          <div className="mmsip-section">
            <h2 className="mmsip-h2">Start Your Multi-Metal SIP with CoinBazaar powered by eBullion</h2>
            <p className="mmsip-p">
              Building a diversified precious metals portfolio has never been easier. With
              CoinBazaar powered by eBullion's Multi-Metal SIP, you can invest in Gold, Silver,
              Platinum and Palladium through a single SIP setup while maintaining complete
              control over your allocation.
            </p>
            <p className="mmsip-p">
              Choose the metals you want to invest in, decide the amount for each metal, and
              start building your precious metals portfolio with investments starting from just
              ₹10 per metal.
            </p>
            <p className="mmsip-p mmsip-closing">
              Invest regularly. Diversify intelligently. Build wealth systematically with
              CoinBazaar powered by eBullion Multi-Metal SIP. Start Your Multi-Metal SIP Today.
            </p>
            <button className="mmsip-cta-btn" onClick={startMultimetalSip}>
              Start Multi-Metal SIP
            </button>
          </div>

          {/* ── FAQ ── */}
          <div className="mmsip-section">
            <h2 className="mmsip-h2">Frequently Asked Questions</h2>
            <FaqAccordion />
          </div>

        </div>
      </div>
    </PageLayout>
  );
};

export default MultimetalSipPage;
